import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ProviderVerificationStatus } from '@gramio/shared';
import { providerManagementService, PlatformProvider, ListProvidersParams } from '../services/providerManagement';
import { Table, TableColumn } from '../components/Table';
import './ProvidersPage.css';

export const ProvidersPage: React.FC = () => {
  const navigate = useNavigate();
  const [providers, setProviders] = useState<PlatformProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all');
  const [verificationFilter, setVerificationFilter] = useState<ProviderVerificationStatus | 'all'>('all');
  const [accountFilter, setAccountFilter] = useState<'all' | 'active' | 'suspended' | 'disabled'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const itemsPerPage = 50;

  useEffect(() => {
    loadProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadProviders reads from state
  }, [currentPage, serviceTypeFilter, searchTerm, verificationFilter, accountFilter]);

  const loadProviders = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListProvidersParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        serviceType: serviceTypeFilter !== 'all' ? serviceTypeFilter : undefined,
        verificationStatus: verificationFilter !== 'all' ? verificationFilter : undefined,
        adminAccountStatus: accountFilter !== 'all' ? accountFilter : undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await providerManagementService.listProviders(params);
      setProviders(response.providers);
      setTotalCount(response.count);
      setLastEvaluatedKey(
        response.lastEvaluatedKey
          ? encodeURIComponent(JSON.stringify(response.lastEvaluatedKey))
          : undefined
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load providers');
      console.error('Error loading providers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadProviders();
  };

  const handleServiceTypeFilter = (type: string) => {
    setServiceTypeFilter(type);
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleViewProvider = (providerId: string) => {
    navigate(`/dashboard/providers/${providerId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: TableColumn<PlatformProvider>[] = useMemo(() => [
    {
      header: 'Name/Email',
      accessor: (row) => (
        <div>
          <div>{row.email || 'N/A'}</div>
          {row.phone && (
            <div className="secondary-text">{row.phone}</div>
          )}
        </div>
      ),
    },
    {
      header: 'Location',
      accessor: (row) => {
        if (row.location) {
          return (
            <div>
              <div>{row.location.city}, {row.location.state}</div>
              <div className="secondary-text">{row.location.pincode}</div>
            </div>
          );
        }
        return 'N/A';
      },
      hideOnTablet: true,
    },
    {
      header: 'Service Types',
      accessor: (row) => (
        <div className="service-types">
          {row.serviceTypes?.slice(0, 2).map((type, idx) => (
            <span key={idx} className="service-type-badge">
              {type}
            </span>
          ))}
          {row.serviceTypes && row.serviceTypes.length > 2 && (
            <span className="service-type-badge">
              +{row.serviceTypes.length - 2}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Rating',
      accessor: (row) => (
        <div>
          <div className="rating-value">
            {row.ratings?.average?.toFixed(1) || 'N/A'}
          </div>
          <div className="secondary-text">
            ({row.ratings?.count || 0} reviews)
          </div>
        </div>
      ),
      hideOnTablet: true,
    },
    {
      header: 'Verification',
      accessor: (row) => (
        <span className={`verification-status ${row.verification?.status || 'unverified'}`}>
          {row.verification?.status || 'unverified'}
        </span>
      ),
    },
    {
      header: 'Account',
      accessor: (row) => {
        const s = row.adminAccount?.status ?? 'active';
        return (
          <span className={`account-status-pill account-status-${s}`}>
            {s}
          </span>
        );
      },
      hideOnTablet: true,
    },
    {
      header: 'Status',
      accessor: (row) => (
        <span className={`status-badge ${row.availability?.status || 'unavailable'}`}>
          {row.availability?.status || 'unavailable'}
        </span>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => formatDate(value as string),
      hideOnMobile: true,
    },
    {
      header: 'Actions',
      accessor: (row) => (
        <button
          className="view-button"
          onClick={() => handleViewProvider(row.providerId)}
        >
          View
        </button>
      ),
      dataLabel: '',
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleViewProvider is stable
  ], []);

  const handleExport = () => {
    const csvContent = [
      ['Provider ID', 'User ID', 'Email', 'Phone', 'Service Types', 'Verification', 'Account', 'Availability', 'Created'].join(','),
      ...providers.map(p => [
        p.providerId,
        p.userId,
        p.email || '',
        p.phone || '',
        p.serviceTypes.join(';'),
        p.verification?.status ?? 'unverified',
        p.adminAccount?.status ?? 'active',
        p.availability.status,
        p.createdAt,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `providers-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="providers-page">
      <div className="providers-header">
        <h1>Provider Management</h1>
        <div className="providers-stats">
          <div className="stat-card">
            <div className="stat-value">{totalCount}</div>
            <div className="stat-label">Total Providers</div>
          </div>
          <button onClick={handleExport} className="export-button" style={{marginLeft: '16px', padding: '8px 16px', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500'}}>
            Export CSV
          </button>
        </div>
      </div>

      <div className="providers-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name, email, skills, or bio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">
            Search
          </button>
        </form>

        <div className="filter-row-secondary">
          <label className="filter-label">
            ID verification
            <select
              value={verificationFilter}
              onChange={(e) => {
                setVerificationFilter(e.target.value as ProviderVerificationStatus | 'all');
                setCurrentPage(1);
                setLastEvaluatedKey(undefined);
              }}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="unverified">Unverified</option>
              <option value="pending_review">Pending review</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </label>
          <label className="filter-label">
            Admin account
            <select
              value={accountFilter}
              onChange={(e) => {
                setAccountFilter(e.target.value as typeof accountFilter);
                setCurrentPage(1);
                setLastEvaluatedKey(undefined);
              }}
              className="filter-select"
            >
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
        </div>

        <div className="filter-buttons">
          <button
            className={`filter-button ${serviceTypeFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleServiceTypeFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-button ${serviceTypeFilter === 'one-time' ? 'active' : ''}`}
            onClick={() => handleServiceTypeFilter('one-time')}
          >
            One-Time
          </button>
          <button
            className={`filter-button ${serviceTypeFilter === 'weekly' ? 'active' : ''}`}
            onClick={() => handleServiceTypeFilter('weekly')}
          >
            Weekly
          </button>
          <button
            className={`filter-button ${serviceTypeFilter === 'monthly' ? 'active' : ''}`}
            onClick={() => handleServiceTypeFilter('monthly')}
          >
            Monthly
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading providers...</div>
      ) : (
        <>
          <Table
            columns={columns}
            data={providers}
            getRowId={(row) => row.providerId}
            emptyMessage="No providers found"
            containerClassName="providers-table-container"
            className="providers-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || providers.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

